import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type { DataResponse, PorezVysledok, ZhodaCutRite } from "@/api/schemas";

/** Posledný výsledok optimalizácie porezu varianty + aktuálnosť. */
export function usePorezVysledok(
  variantaId: string | undefined,
): UseQueryResult<DataResponse<PorezVysledok>> {
  return useQuery({
    queryKey: queryKeys.zakazky.porez(variantaId ?? ""),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<PorezVysledok>>(
        `/varianty/${variantaId}/porez`,
      );
      return data;
    },
    enabled: !!variantaId,
  });
}

/** Spustí optimalizáciu porezu — výsledok vstúpi do cien zákazky. */
export function useSpustiPorez(
  variantaId: string,
): UseMutationResult<DataResponse<PorezVysledok>, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<DataResponse<PorezVysledok>>(
        `/varianty/${variantaId}/porez`,
      );
      return data;
    },
    // Mení ceny celej zákazky — invaliduj všetky zakazky query
    // (pokrýva aj queryKeys.zakazky.porez).
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.all }),
  });
}

/** Zruší výsledok porezu — ceny sa vrátia na default nadmieru. */
export function useZrusPorez(
  variantaId: string,
): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/varianty/${variantaId}/porez`);
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.all }),
  });
}

/**
 * Odkiaľ cena berie odpad plošných (KOLO 213): `porez` = plán z Cut Rite,
 * inak porez Rozpočtu; `pausal` = 20 / 30 % z cenníka. Mení ceny zákazky.
 */
export function useOdpadVCene(
  variantaId: string,
): UseMutationResult<void, Error, "porez" | "pausal"> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (odpad) => {
      await api.patch(`/varianty/${variantaId}`, { odpad_v_cene: odpad });
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.all }),
  });
}

/**
 * Porez Rozpočtu zo súborov poslaných do Cut Rite vedľa jeho plánov —
 * Nastavenia → Rezanie → Zhoda s Cut Rite (KOLO 213).
 */
export function useZhodaCutRite(): UseQueryResult<DataResponse<ZhodaCutRite>> {
  return useQuery({
    queryKey: ["cutrite", "zhoda"],
    queryFn: async () => {
      const { data } = await api.get<DataResponse<ZhodaCutRite>>("/cutrite/zhoda");
      return data;
    },
  });
}
